
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTreatmentSessions } from '@/hooks/useTreatmentSessions';
import { format, parseISO, addDays } from 'date-fns';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle, 
  CardFooter 
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Calendar,
  CalendarDays, 
  ClipboardEdit, 
  Edit, 
  FileImage, 
  FilePlus, 
  RotateCw,
  CheckCircle2,
  XCircle,
  Clock,
  User,
  RefreshCw
} from 'lucide-react';
import DoctorHeader from '@/components/doctor/DoctorHeader';
import DoctorFooterNav from '@/components/doctor/DoctorFooterNav';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { TreatmentSession, SessionStatus } from '@/types/treatmentSession';
import SessionNotesEditor from './components/SessionNotesEditor';
import SessionDocumentsManager from './components/SessionDocumentsManager';
import SessionPhotosManager from './components/SessionPhotosManager';
import SessionScheduler from './components/SessionScheduler';
import AddSessionForm from './components/AddSessionForm';

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'upcoming':
      return 'bg-blue-100 text-blue-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    case 'rescheduled':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const PatientSessionsManager = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const { 
    sessions, 
    isLoading, 
    error,
    uploadDocument,
    uploadPhoto,
    updateSessionStatus,
    updateSessionDate,
    updateDoctorNotes,
    addSession,
    getUpcomingSessionNotifications
  } = useTreatmentSessions(patientId || '1');
  
  const [selectedSession, setSelectedSession] = useState<TreatmentSession | null>(null);
  const [activeAction, setActiveAction] = useState<'notes' | 'documents' | 'photos' | 'reschedule' | null>(null);
  const [showAddSessionDialog, setShowAddSessionDialog] = useState(false);

  const handleUpdateStatus = async (sessionId: number, status: SessionStatus) => {
    await updateSessionStatus(sessionId, status);
  };
  
  const openNotesEditor = (session: TreatmentSession) => {
    setSelectedSession(session);
    setActiveAction('notes');
  };
  
  const openDocumentsManager = (session: TreatmentSession) => {
    setSelectedSession(session);
    setActiveAction('documents');
  };
  
  const openPhotosManager = (session: TreatmentSession) => {
    setSelectedSession(session);
    setActiveAction('photos');
  };
  
  const openRescheduleDialog = (session: TreatmentSession) => {
    setSelectedSession(session);
    setActiveAction('reschedule');
  };
  
  const closeDialog = () => {
    setSelectedSession(null);
    setActiveAction(null);
  };
  
  const handleSaveNotes = async (notes: string) => {
    if (selectedSession) {
      await updateDoctorNotes(selectedSession.id, notes);
      closeDialog();
    }
  };
  
  const handleReschedule = async (newDate: string) => {
    if (selectedSession) {
      await updateSessionDate(selectedSession.id, newDate);
      closeDialog();
    }
  };
  
  const handleAddSession = async (sessionData: any) => {
    await addSession({
      ...sessionData,
      planId: parseInt(patientId || '1'),
      doctorId: 1, // In a real app, this would be from context or auth
      doctorName: "Dr. James Wilson", // In a real app, this would be from context or auth
      paymentStatus: 'unpaid',
      patientAttended: false
    });
    setShowAddSessionDialog(false);
  };
  
  const upcomingSessions = sessions.filter(session => session.status === 'upcoming');
  const completedSessions = sessions.filter(session => session.status === 'completed');
  const cancelledSessions = sessions.filter(session => ['cancelled', 'rescheduled'].includes(session.status));

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted/30 pb-20 pt-16">
        <DoctorHeader />
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="flex items-center justify-center min-h-[300px]">
              <p className="text-muted-foreground">Loading patient sessions...</p>
            </CardContent>
          </Card>
        </div>
        <DoctorFooterNav />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-muted/30 pb-20 pt-16">
        <DoctorHeader />
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="flex items-center justify-center min-h-[300px]">
              <p className="text-red-500">{error}</p>
            </CardContent>
          </Card>
        </div>
        <DoctorFooterNav />
      </div>
    );
  }

  // Check for doctor notifications
  const notifications = getUpcomingSessionNotifications();
  
  return (
    <div className="min-h-screen bg-muted/30 pb-20 pt-16">
      <DoctorHeader />
      
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Patient Treatment Sessions</h1>
          <Button onClick={() => setShowAddSessionDialog(true)}>
            <FilePlus className="h-4 w-4 mr-2" />
            Add Session
          </Button>
        </div>
        
        {notifications.length > 0 && (
          <Card className="mb-6 border-l-4 border-l-blue-500">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-blue-500 mt-0.5" />
                <div>
                  <h3 className="font-medium">Upcoming Session Reminder</h3>
                  <p className="text-sm text-muted-foreground">{notifications[0].message.replace("You", "Patient")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        <Tabs defaultValue="upcoming" className="mb-6">
          <TabsList className="mb-4">
            <TabsTrigger value="upcoming">
              Upcoming Sessions ({upcomingSessions.length})
            </TabsTrigger>
            <TabsTrigger value="completed">
              Completed Sessions ({completedSessions.length})
            </TabsTrigger>
            <TabsTrigger value="cancelled">
              Cancelled/Rescheduled ({cancelledSessions.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="upcoming">
            {upcomingSessions.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No upcoming sessions</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowAddSessionDialog(true)}
                  >
                    <FilePlus className="h-4 w-4 mr-2" />
                    Schedule New Session
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {upcomingSessions.map((session) => (
                  <Card key={session.id} className="overflow-hidden border-l-4 border-l-blue-500">
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle>{session.title}</CardTitle>
                          <CardDescription>Session #{session.sessionNumber}</CardDescription>
                        </div>
                        <Badge className={getStatusColor(session.status)}>
                          {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                        </Badge>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pb-2">
                      <div className="space-y-2">
                        <div className="flex items-center text-sm">
                          <CalendarDays className="h-4 w-4 mr-2 text-muted-foreground" />
                          <span>
                            {format(parseISO(session.date), 'EEEE, MMMM d, yyyy')} at {format(parseISO(session.date), 'h:mm a')}
                          </span>
                        </div>
                        
                        <div className="flex items-center text-sm">
                          <User className="h-4 w-4 mr-2 text-muted-foreground" />
                          <span>Patient: {session.patientAttended ? 'Attended' : 'Not yet attended'}</span>
                        </div>
                        
                        {session.description && (
                          <p className="text-sm">{session.description}</p>
                        )}
                      </div>
                    </CardContent>
                    
                    <CardFooter className="flex justify-between pt-2">
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openNotesEditor(session)}
                        >
                          <ClipboardEdit className="h-3 w-3 mr-1" />
                          Notes
                        </Button>
                        
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openDocumentsManager(session)}
                        >
                          <FilePlus className="h-3 w-3 mr-1" />
                          Docs
                        </Button>
                        
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openPhotosManager(session)}
                        >
                          <FileImage className="h-3 w-3 mr-1" />
                          Photos
                        </Button>
                        
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openRescheduleDialog(session)}
                        >
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Reschedule
                        </Button>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleUpdateStatus(session.id, 'cancelled')}
                        >
                          <XCircle className="h-3 w-3 mr-1" />
                          Cancel
                        </Button>
                        
                        <Button 
                          size="sm"
                          onClick={() => handleUpdateStatus(session.id, 'completed')}
                        >
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Complete
                        </Button>
                      </div>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="completed">
            {completedSessions.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <CheckCircle2 className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No completed sessions yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {completedSessions.map((session) => (
                  <Card key={session.id} className="overflow-hidden border-l-4 border-l-green-500">
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle>{session.title}</CardTitle>
                          <CardDescription>Session #{session.sessionNumber}</CardDescription>
                        </div>
                        <Badge className={getStatusColor(session.status)}>
                          {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                        </Badge>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pb-2">
                      <div className="space-y-2">
                        <div className="flex items-center text-sm">
                          <CalendarDays className="h-4 w-4 mr-2 text-muted-foreground" />
                          <span>
                            {format(parseISO(session.date), 'EEEE, MMMM d, yyyy')} at {format(parseISO(session.date), 'h:mm a')}
                          </span>
                        </div>
                        
                        {session.doctorNotes && (
                          <div className="text-sm bg-muted/50 p-2 rounded">
                            <p className="font-medium mb-1">Notes:</p>
                            <p>{session.doctorNotes}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                    
                    <CardFooter className="flex justify-between pt-2">
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openNotesEditor(session)}
                        >
                          <ClipboardEdit className="h-3 w-3 mr-1" />
                          Edit Notes
                        </Button>
                        
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openDocumentsManager(session)}
                        >
                          <FilePlus className="h-3 w-3 mr-1" />
                          Manage Docs
                        </Button>
                        
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openPhotosManager(session)}
                        >
                          <FileImage className="h-3 w-3 mr-1" />
                          Manage Photos
                        </Button>
                      </div>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="cancelled">
            {cancelledSessions.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <XCircle className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No cancelled or rescheduled sessions</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {cancelledSessions.map((session) => (
                  <Card 
                    key={session.id} 
                    className={`overflow-hidden border-l-4 ${
                      session.status === 'cancelled' ? 'border-l-red-500' : 'border-l-yellow-500'
                    }`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle>{session.title}</CardTitle>
                          <CardDescription>Session #{session.sessionNumber}</CardDescription>
                        </div>
                        <Badge className={getStatusColor(session.status)}>
                          {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                        </Badge>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pb-2">
                      <div className="space-y-2">
                        <div className="flex items-center text-sm">
                          <CalendarDays className="h-4 w-4 mr-2 text-muted-foreground" />
                          <span>
                            {format(parseISO(session.date), 'EEEE, MMMM d, yyyy')} at {format(parseISO(session.date), 'h:mm a')}
                          </span>
                        </div>
                        
                        {session.doctorNotes && (
                          <div className="text-sm bg-muted/50 p-2 rounded">
                            <p className="font-medium mb-1">Notes:</p>
                            <p>{session.doctorNotes}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                    
                    <CardFooter className="flex justify-between pt-2">
                      {session.status === 'cancelled' ? (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openRescheduleDialog(session)}
                        >
                          <RotateCw className="h-3 w-3 mr-1" />
                          Reschedule
                        </Button>
                      ) : (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openNotesEditor(session)}
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          View Notes
                        </Button>
                      )}
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Notes Editor Dialog */}
      <Dialog open={activeAction === 'notes' && !!selectedSession} onOpenChange={closeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Session Notes</DialogTitle>
            <DialogDescription>
              Update notes for session #{selectedSession?.sessionNumber} - {selectedSession?.title}
            </DialogDescription>
          </DialogHeader>
          
          {selectedSession && (
            <SessionNotesEditor
              session={selectedSession}
              onSave={handleSaveNotes}
              onCancel={closeDialog}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Documents Manager Dialog */}
      <Dialog open={activeAction === 'documents' && !!selectedSession} onOpenChange={closeDialog}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Manage Documents</DialogTitle>
            <DialogDescription>
              Upload and manage documents for session #{selectedSession?.sessionNumber} - {selectedSession?.title}
            </DialogDescription>
          </DialogHeader>
          
          {selectedSession && (
            <SessionDocumentsManager
              session={selectedSession}
              onUploadDocument={uploadDocument}
              onClose={closeDialog}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Photos Manager Dialog */}
      <Dialog open={activeAction === 'photos' && !!selectedSession} onOpenChange={closeDialog}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Manage Photos</DialogTitle>
            <DialogDescription>
              Upload and manage before/after photos for session #{selectedSession?.sessionNumber} - {selectedSession?.title}
            </DialogDescription>
          </DialogHeader>
          
          {selectedSession && (
            <SessionPhotosManager
              session={selectedSession}
              onUploadPhoto={uploadPhoto}
              onClose={closeDialog}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Reschedule Dialog */}
      <Dialog open={activeAction === 'reschedule' && !!selectedSession} onOpenChange={closeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reschedule Session</DialogTitle>
            <DialogDescription>
              Change the date and time for session #{selectedSession?.sessionNumber} - {selectedSession?.title}
            </DialogDescription>
          </DialogHeader>
          
          {selectedSession && (
            <SessionScheduler
              session={selectedSession}
              onReschedule={handleReschedule}
              onCancel={closeDialog}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Add Session Dialog */}
      <Dialog open={showAddSessionDialog} onOpenChange={setShowAddSessionDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Add New Session</DialogTitle>
            <DialogDescription>
              Schedule a new treatment session for this patient
            </DialogDescription>
          </DialogHeader>
          
          <AddSessionForm
            onSubmit={handleAddSession}
            onCancel={() => setShowAddSessionDialog(false)}
            sessionNumber={sessions.length + 1}
          />
        </DialogContent>
      </Dialog>
      
      <DoctorFooterNav />
    </div>
  );
};

export default PatientSessionsManager;
